import React from 'react';
import { ActivityIcon, GlobeIcon, LockIcon, PlusIcon } from 'lucide-react';
import type { HealthStatus } from '../../types/monitor';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { QuotaMeter } from '../../components/QuotaMeter';
import { EmptyState } from '../../components/EmptyState';
import { WatchtowerMark } from '../../components/WatchtowerMark';
import { GridBackdrop } from '../../components/GridBackdrop';
import { Wordmark } from '../../components/Wordmark';
import { CertSeal } from '../../components/brand/CertSeal';
import { SignalArc } from '../../components/brand/SignalArc';
import { Beacon } from '../../components/brand/Beacon';
import { DarkTower } from '../../components/brand/DarkTower';
import { ArcQuota } from '../../components/brand/ArcQuota';
import { markVariants } from '../../components/brand/markVariants';
const markSizes = [{
  size: 'h-4 w-4',
  label: '16'
}, {
  size: 'h-5 w-5',
  label: '20'
}, {
  size: 'h-6 w-6',
  label: '24'
}, {
  size: 'h-8 w-8',
  label: '32'
}, {
  size: 'h-12 w-12',
  label: '48'
}];
const statuses: HealthStatus[] = ['healthy', 'warning', 'critical', 'paused'];
const statusTone: Record<HealthStatus, string> = {
  healthy: 'bg-healthy-soft text-healthy-ink border-healthy-border',
  warning: 'bg-warn-soft text-warn-ink border-warn-border',
  critical: 'bg-critical-soft text-critical-ink border-critical-border',
  paused: 'bg-paused-soft text-paused-ink border-paused-border'
};
const statusLabel: Record<HealthStatus, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
  paused: 'Not monitored'
};
const senders = [{
  address: 'alerts@watchtowerworks.com',
  use: 'Expiry warnings, downtime, recovery'
}, {
  address: 'billing@watchtowerworks.com',
  use: 'Receipts, card failures, plan changes'
}, {
  address: 'status@watchtowerworks.com',
  use: 'Our own incidents and maintenance'
}];
export function Brand() {
  return <>
      <section className="relative overflow-hidden bg-shell" data-id="element-1292" data-mp="2rg6yx52xf0dl">
        <GridBackdrop opacity={0.4} data-id="element-1293" data-mp="2rit796xn5jwv" />
        <div className="relative mx-auto w-full max-w-page px-5 py-14 sm:px-8" data-id="element-1294" data-mp="2moxcedcocd5x">
          <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.16em] text-brand-bright" data-id="element-1295" data-mp="2hqvqzlrs01ra">
            <span className="h-px w-8 bg-brand-bright/60" aria-hidden="true" data-id="element-1296" data-mp="2crsyf0szfo07" />
            specimen · not applied
          </p>
          <h1 className="mt-5 max-w-3xl text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.8rem]" data-id="element-1297" data-mp="27qwjztb08jgj">
            Proposed icons and branding for Watchtower
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/65" data-id="element-1298" data-mp="1ytcicj98cl5e">
            Everything below is rendered for review only. Start with the eight
            mark directions — tell me a letter and I will run it through the
            header, sidebar, emails, invoices and favicon.
          </p>

          <div className="mt-8 flex flex-wrap gap-x-7 gap-y-5" data-id="element-1299" data-mp="4jnum4gxf53p">
            {markVariants.map(variant => <span key={variant.id} className="text-center" data-id="element-1300" data-mp="9gxt09mamr3o">
                <variant.Mark className="mx-auto h-8 w-8 text-brand-bright" data-id="element-1301" data-mp="eb1yspjtot36" />
                <span className="mt-2 block font-mono text-[11px] text-white/45" data-id="element-1302" data-mp="j93fx4xouic0">
                  {variant.id}
                </span>
              </span>)}
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-page gap-5 px-5 py-12 sm:px-8" data-id="element-1303" data-mp="3mqlml91q5jau">
        <Panel as="section" className="border-brand-border" data-id="element-1304" data-mp="3ro3wp2rha4kf">
          <PanelHeader title="0 · Eight directions for the mark" description="Pick one and I will run it through the whole product. Each is shown large, then at 16 / 20 / 24px, then reversed on navy, then locked up with the wordmark." level={2} data-id="element-1305" data-mp="3rqzyj26g5de5" />
          <div className="grid gap-px bg-line sm:grid-cols-2 xl:grid-cols-4" data-id="element-1306" data-mp="1b22tbjij1yi9">
            {markVariants.map(variant => <article key={variant.id} className="bg-surface" data-id="element-1307" data-mp="14jzmd07yntkw">
                <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5" data-id="element-1308" data-mp="14lkl721b5yok">
                  <span className="font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-brand" data-id="element-1309" data-mp="1jexk68lvpb9d">
                    {variant.id}
                  </span>
                  <span className="text-[13px] font-semibold text-ink" data-id="element-1310" data-mp="1sgdqa45nur7s">
                    {variant.name}
                  </span>
                </div>

                <div className="flex h-[128px] items-center justify-center bg-canvas" data-id="element-1311" data-mp="2278g1ii488xz">
                  <variant.Mark className="h-16 w-16 text-brand" data-id="element-1312" data-mp="1x8f0lkj9q7v6" />
                </div>

                <div className="flex items-end justify-center gap-5 border-y border-line px-4 py-4" data-id="element-1313" data-mp="1xarftpxn644j">
                  <variant.Mark className="h-4 w-4 text-ink" data-id="element-1314" data-mp="2b5ttx0cn6zsf" />
                  <variant.Mark className="h-5 w-5 text-ink" data-id="element-1315" data-mp="277w1zprhlzlc" />
                  <variant.Mark className="h-6 w-6 text-ink" data-id="element-1316" data-mp="2pudeqhm2v73f" />
                </div>

                <div className="flex h-[92px] items-center justify-center gap-4 bg-shell" data-id="element-1317" data-mp="2utzoui6m0780">
                  <variant.Mark className="h-9 w-9 text-brand-bright" data-id="element-1318" data-mp="2gu74r5pl8p90" />
                  <variant.Mark className="h-5 w-5 text-white" data-id="element-1319" data-mp="2gtxdlest4c1q" />
                </div>

                <div className="flex items-center gap-2.5 border-t border-line px-4 py-3.5" data-id="element-1320" data-mp="2lrqhg16n0tyu">
                  <variant.Mark className="h-7 w-7 shrink-0 text-brand" data-id="element-1321" data-mp="273lz4ybhykgl" />
                  <span className="font-mono text-[13px] font-medium uppercase tracking-[0.14em] text-ink" data-id="element-1322" data-mp="2bvdq39ykes79">
                    Watchtower
                  </span>
                </div>

                <p className="border-t border-line bg-canvas px-4 py-3 text-xs leading-relaxed text-ink-muted" data-id="element-1323" data-mp="22514rfixev02">
                  {variant.note}
                </p>
              </article>)}
          </div>
        </Panel>

        <Panel as="section" data-id="element-1324" data-mp="1t3b5jnisyrrq">
          <PanelHeader title="1 · The mark at size" description="Lighthouse silhouette throwing a lit beam. The beam drops away at 16px, where it stops resolving." level={2} data-id="element-1325" data-mp="1t6p74tjn6wy4" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1326" data-mp="3m1huoxnoyy2d">
            <div className="flex flex-wrap items-end gap-8 bg-surface p-6" data-id="element-1327" data-mp="3m1q6em82vxbz">
              {markSizes.map(item => <span key={item.label} className="text-center" data-id="element-1328" data-mp="3h0kaygn19vyr">
                  <WatchtowerMark beam={item.label !== '16'} className={`${item.size} text-brand`} data-id="element-1329" data-mp="hlypmazx957k" />
                  <span className="mt-2 block font-mono text-[10px] text-ink-soft" data-id="element-1330" data-mp="8mbsw2i50nku">
                    {item.label}px
                  </span>
                </span>)}
            </div>
            <div className="flex flex-wrap items-end gap-8 bg-shell p-6" data-id="element-1331" data-mp="2tcj5jgl5ivnk">
              {markSizes.map(item => <span key={item.label} className="text-center" data-id="element-1332" data-mp="2odgbssamgbuw">
                  <WatchtowerMark beam={item.label !== '16'} className={`${item.size} text-white`} data-id="element-1333" data-mp="2e9sz6pww724h" />
                  <span className="mt-2 block font-mono text-[10px] text-white/40" data-id="element-1334" data-mp="2j9dw6usi50ek">
                    {item.label}px
                  </span>
                </span>)}
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1335" data-mp="2t0idi15ngxck">
          <PanelHeader title="2 · Wordmark lockups" description="The approved lockup at three sizes, on light and on the shell." level={2} data-id="element-1336" data-mp="2x0sipblmbjqd" />
          <div className="grid gap-px bg-line md:grid-cols-3" data-id="element-1337" data-mp="36yq47rc3f2fa">
            {[{
            size: 'sm' as const,
            subline: true,
            name: 'Small · headers, sidebars',
            note: 'The default. Mark at 28px with the WORKS line intact.'
          }, {
            size: 'md' as const,
            subline: true,
            name: 'Medium · auth, invoices',
            note: 'For quieter pages that can carry a larger lockup.'
          }, {
            size: 'sm' as const,
            subline: false,
            name: 'Compact · tight bars',
            note: 'WORKS dropped where vertical space runs out.'
          }].map(option => <div key={option.name} className="bg-surface" data-id="element-1338" data-mp="30iq6daedfhci">
                <div className="flex h-[104px] items-center justify-center border-b border-line bg-canvas px-5" data-id="element-1339" data-mp="2vjwvlpqgy4g1">
                  <Wordmark size={option.size} showSubline={option.subline} tone="light" data-id="element-1340" data-mp="39jxsku3m4rq8" />
                </div>
                <div className="flex h-[104px] items-center justify-center border-b border-line bg-shell px-5" data-id="element-1341" data-mp="orvzadwd1dp8">
                  <Wordmark size={option.size} showSubline={option.subline} tone="dark" data-id="element-1342" data-mp="kqagyd12477n" />
                </div>
                <div className="px-5 py-4" data-id="element-1343" data-mp="xapl09msa38v">
                  <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1344" data-mp="x7lcx1676ncx">
                    {option.name}
                  </p>
                  <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-1345" data-mp="ncjltzsd3b7p">
                    {option.note}
                  </p>
                </div>
              </div>)}
          </div>
        </Panel>

        <Panel as="section" data-id="element-1346" data-mp="1fyukv60bl42e">
          <PanelHeader title="2b · App icon and favicon" description="The tower alone, no wordmark. Centred in the tile with the beam clearing the right edge." level={2} data-id="element-1347" data-mp="1g28mgc15t98s" />
          <div className="flex flex-wrap items-end gap-8 px-5 py-6 sm:px-6" data-id="element-1348" data-mp="1tzx83m31wkew">
            {([{
            tile: 'bg-brand',
            mark: 'text-white',
            label: '512 · brand'
          }, {
            tile: 'bg-shell',
            mark: 'text-white',
            label: '512 · shell'
          }, {
            tile: 'bg-canvas border border-line',
            mark: 'text-brand',
            label: '512 · light'
          }] as const).map(option => <span key={option.label} className="text-center" data-id="element-1349" data-mp="11ltghy5n0f3p">
                <span className={`flex h-20 w-20 items-center justify-center rounded-[18px] ${option.tile}`} data-id="element-1350" data-mp="11k8nwe2vv68h">
                  <WatchtowerMark className={`h-12 w-12 ${option.mark}`} data-id="element-1351" data-mp="rndpdv6m15u7" />
                </span>
                <span className="mt-2.5 block font-mono text-[10px] text-ink-soft" data-id="element-1352" data-mp="dwh830i4wb2g">
                  {option.label}
                </span>
              </span>)}

            <span className="text-center" data-id="element-1353" data-mp="it7knduqcoj4">
              <span className="flex h-20 w-20 items-center justify-center rounded-[18px] bg-brand" data-id="element-1354" data-mp="3xrqz9y2aun9">
                <WatchtowerMark beam={false} className="h-12 w-12 text-white" data-id="element-1355" data-mp="8xe3fh62g6v0" />
              </span>
              <span className="mt-2.5 block font-mono text-[10px] text-ink-soft" data-id="element-1356" data-mp="3lfyqex0lnpth">
                32 · no beam
              </span>
            </span>

            <span className="flex items-end gap-4 pb-6" data-id="element-1357" data-mp="3hh7x70z98sbn">
              <WatchtowerMark className="h-8 w-8 text-brand" data-id="element-1358" data-mp="3vfy04x8r9m7x" />
              <WatchtowerMark className="h-6 w-6 text-brand" data-id="element-1359" data-mp="3qg36wn1ickjp" />
              <WatchtowerMark beam={false} className="h-4 w-4 text-brand" data-id="element-1360" data-mp="3qeibywba6zlb" />
            </span>
          </div>
          <p className="border-t border-line bg-canvas px-5 py-3.5 text-[13px] leading-relaxed text-ink-muted sm:px-6" data-id="element-1361" data-mp="883y0xd0ipmx">
            Use the brand-green tile as the primary app icon and the beamless
            version at 32px and below. I cannot install a favicon from here —
            upload the exported square through the{' '}
            <strong data-id="element-1362" data-mp="rxo5o4hd5rtd">PUBLISH</strong> button in the top right.
          </p>
        </Panel>

        <Panel as="section" data-id="element-1363" data-mp="mzcvfsn5pfam">
          <PanelHeader title="3 · Service glyphs" description="Custom replacements for the two Lucide icons carrying the most meaning in the product." level={2} data-id="element-1364" data-mp="n05us8jsd2iq" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1365" data-mp="z25r2qu59brs">
            <div className="bg-surface p-6" data-id="element-1366" data-mp="140qs2sh7zzha">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1367" data-mp="13yw8bhhoqdes">
                Certificate expiry
              </p>
              <div className="mt-5 flex items-end gap-8" data-id="element-1368" data-mp="1dqk41ul9ygm2">
                <span className="text-center" data-id="element-1369" data-mp="1dq0kyajw1m6g">
                  <LockIcon className="mx-auto h-8 w-8 text-brand" aria-hidden="true" data-id="element-1370" data-mp="1hrcegr5vurjx" />
                  <span className="mt-3 block text-xs text-ink-soft" data-id="element-1371" data-mp="1mrhxxk2017qx">
                    current
                  </span>
                </span>
                <span className="text-center" data-id="element-1372" data-mp="3oil8yafswoc0">
                  <CertSeal className="mx-auto h-8 w-8 text-brand" data-id="element-1373" data-mp="3ojwcqa98xw34" />
                  <span className="mt-3 block text-xs font-medium text-ink" data-id="element-1374" data-mp="3tgmt6yoyqtp2">
                    CertSeal
                  </span>
                </span>
                <span className="flex items-end gap-3 pb-6" data-id="element-1375" data-mp="3m002o18sswxj">
                  <CertSeal className="h-4 w-4 text-ink-muted" data-id="element-1376" data-mp="3h65ikwpa6fxx" />
                  <CertSeal className="h-5 w-5 text-ink-muted" data-id="element-1377" data-mp="3c7uaaqemwdht" />
                  <CertSeal className="h-6 w-6 text-ink-muted" data-id="element-1378" data-mp="3c5zol9vjghco" />
                </span>
              </div>
              <p className="mt-5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1379" data-mp="376wlhuz4cl7c">
                A stamped seal with the beacon lit at its centre — a certificate
                is an attestation, and this reads as one.
              </p>
            </div>

            <div className="bg-surface p-6" data-id="element-1380" data-mp="3uxk5hemb71a9">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1381" data-mp="3v0od6lyzgf5o">
                Uptime
              </p>
              <div className="mt-5 flex items-end gap-8" data-id="element-1382" data-mp="2jjy6l1qndlbj">
                <span className="text-center" data-id="element-1383" data-mp="2jhbzt4bkz9ud">
                  <ActivityIcon className="mx-auto h-8 w-8 text-brand" aria-hidden="true" data-id="element-1384" data-mp="2ek2bjrzxh484" />
                  <span className="mt-3 block text-xs text-ink-soft" data-id="element-1385" data-mp="7724s7zu4q82">
                    current
                  </span>
                </span>
                <span className="text-center" data-id="element-1386" data-mp="3th4nyiz9jjoj">
                  <SignalArc className="mx-auto h-8 w-8 text-brand" data-id="element-1387" data-mp="3ti68rf62zkjg" />
                  <span className="mt-3 block text-xs font-medium text-ink" data-id="element-1388" data-mp="2attj5ordh66">
                    SignalArc
                  </span>
                </span>
                <span className="flex items-end gap-3 pb-6" data-id="element-1389" data-mp="uypljx3dukwm">
                  <SignalArc className="h-4 w-4 text-ink-muted" data-id="element-1390" data-mp="uy7lepdlnmjf" />
                  <SignalArc className="h-5 w-5 text-ink-muted" data-id="element-1391" data-mp="g28az830pab4" />
                  <SignalArc className="h-6 w-6 text-ink-muted" data-id="element-1392" data-mp="l0jnjqkp5yx1" />
                </span>
              </div>
              <p className="mt-5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1393" data-mp="30rxqlnbjp04v">
                The mark&apos;s arc repeated outward from a transmitting point.
                Reads as pinging rather than as a generic pulse line.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1394" data-mp="1iu1dscqkl18r">
          <PanelHeader title="4 · Status glyphs — the risky one" description="Beacon signal strength instead of check / triangle / octagon. More ownable, but less instantly recognisable. Compare carefully." level={2} data-id="element-1395" data-mp="1iuuccqfdkkft" />
          <div className="space-y-6 px-5 py-6 sm:px-6" data-id="element-1396" data-mp="152nxr8rvelih">
            <div data-id="element-1397" data-mp="153626soos61h">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1398" data-mp="150jvev9mdukb">
                Current — universal shapes
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-1399" data-mp="1dv7yqfvmx26h">
                {statuses.map(status => <StatusBadge key={status} status={status} size="md" data-id="element-1400" data-mp="1936sbal5e5od" />)}
              </div>
            </div>

            <div data-id="element-1401" data-mp="ld3z2r5hyto6">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1402" data-mp="lckm7oupevh0">
                Proposed — beacon signal
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-1403" data-mp="2ggscqqgawwny">
                {statuses.map(status => <span key={status} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${statusTone[status]}`} data-id="element-1404" data-mp="2lfv97mi7tukb">
                    <Beacon status={status} className="h-3.5 w-3.5" data-id="element-1405" data-mp="2v66jdqshszzn" />
                    {statusLabel[status]}
                  </span>)}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-6 rounded-card border border-line bg-canvas px-5 py-4" data-id="element-1406" data-mp="22j3rhhibnnid">
                {statuses.map(status => <span key={status} className="text-center" data-id="element-1407" data-mp="2cfyp7y6rtjvl">
                    <Beacon status={status} className={`mx-auto h-7 w-7 ${status === 'healthy' ? 'text-healthy' : status === 'warning' ? 'text-warn' : status === 'critical' ? 'text-critical' : 'text-paused'}`} data-id="element-1408" data-mp="2chjk5owzz4tz" />
                    <span className="mt-2 block font-mono text-[10px] text-ink-soft" data-id="element-1409" data-mp="12ge2c694xaqg">
                      {status}
                    </span>
                  </span>)}
              </div>
            </div>

            <p className="max-w-2xl text-[13px] leading-relaxed text-ink-muted" data-id="element-1410" data-mp="shpxyaa2ou58">
              My read: adopt these on the marketing pages and the monitor detail
              header, but keep the universal shapes in the dense monitor list
              and alert log, where people scan for a check or a cross.
            </p>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1411" data-mp="2dw2qv221ehtd">
          <PanelHeader title="5 · The arc as a device" description="The quota meter is the most-seen component in the app. Same information, drawn with the brand's geometry." level={2} data-id="element-1412" data-mp="2dvb3ufmi9sep" />
          <div className="grid gap-px bg-line lg:grid-cols-3" data-id="element-1413" data-mp="1mrrwf3xtq3ek">
            <div className="bg-surface p-6" data-id="element-1414" data-mp="1mu4dlevrc9qk">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1415" data-mp="183x61u7agb4n">
                Current — linear
              </p>
              <div className="mt-4" data-id="element-1416" data-mp="1d0f98pmz01ev">
                <QuotaMeter used={9} limit={15} planName="Pro" showUpgrade={false} data-id="element-1417" data-mp="z2qkh6ps8a4j" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center bg-surface p-6" data-id="element-1418" data-mp="p4sxemjnsjdi">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1419" data-mp="u348f0lowzxb">
                Proposed — arc, on light
              </p>
              <div className="mt-5" data-id="element-1420" data-mp="2jitvv6bar4d2">
                <ArcQuota used={9} limit={15} data-id="element-1421" data-mp="2ohew35qjto1i" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center bg-shell p-6" data-id="element-1422" data-mp="2of14x7yzovz8">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/40" data-id="element-1423" data-mp="cubezydqj1bt">
                Proposed — arc, in sidebar
              </p>
              <div className="mt-5" data-id="element-1424" data-mp="mk4psx61zcf0">
                <ArcQuota used={9} limit={15} tone="dark" data-id="element-1425" data-mp="ml62t76mq8zd" />
              </div>
            </div>
          </div>
          <p className="border-t border-line px-5 py-4 text-[13px] leading-relaxed text-ink-muted sm:px-6" data-id="element-1426" data-mp="hr1twidszqvn">
            Tradeoff: the arc is more distinctive but the linear bar states the
            remainder more plainly, and it carries the &ldquo;3 left on
            Pro&rdquo; sentence underneath. A hybrid works — arc in the sidebar
            where space is tight, linear bar on the add-monitor form and billing
            page where the explanation matters.
          </p>
        </Panel>

        <Panel as="section" data-id="element-1427" data-mp="37c6jvfl4quh2">
          <PanelHeader title="6 · Empty states" description="The one place an illustration earns its keep, because there is no data to look at." level={2} data-id="element-1428" data-mp="32b94fari8ej9" />
          <div className="grid gap-px bg-line lg:grid-cols-2" data-id="element-1429" data-mp="tt3998r53ajx">
            <div className="bg-surface" data-id="element-1430" data-mp="tqqs2xt7h47x">
              <p className="border-b border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1431" data-mp="17krgaxmj78u1">
                Current
              </p>
              <EmptyState icon={GlobeIcon} title="No domains yet" body="Add the first site you would hate to find broken. You can switch on certificate expiry, uptime, or both." action={<Button data-id="element-1433" data-mp="ourzey0uh01p">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1434" data-mp="ov1p0khz956v" />
                    Add a domain
                  </Button>} data-id="element-1432" data-mp="12nhjgwwtbuw6" />
            </div>
            <div className="bg-surface" data-id="element-1435" data-mp="207lwvjg89o28">
              <p className="border-b border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1436" data-mp="20aq8h1w0vm2x">
                Proposed
              </p>
              <div className="flex flex-col items-center px-6 py-10 text-center" data-id="element-1437" data-mp="3d5w0cvohwqut">
                <DarkTower className="h-20 w-32 text-ink-soft" data-id="element-1438" data-mp="3i7bjujb0ypb3" />
                <h3 className="mt-5 text-sm font-semibold text-ink" data-id="element-1439" data-mp="3470rtcxzb4oc">
                  Nothing on watch yet
                </h3>
                <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-muted" data-id="element-1440" data-mp="396vh5c23vm7a">
                  The beacon is unlit. Add the first site you would hate to find
                  broken, and the watch starts on the next sweep.
                </p>
                <div className="mt-5" data-id="element-1441" data-mp="3n5msiqpl2trq">
                  <Button data-id="element-1442" data-mp="3n4k6aw37hfi5">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1443" data-mp="3n5d437k6suo5" />
                    Add a domain
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1444" data-mp="jn1ukdkbbocg">
          <PanelHeader title="7 · Motion signature" description="One rhythm for everything that means live — 1.8s, linear, already used by the ticker badge and the runway header." level={2} data-id="element-1445" data-mp="og4v55niby25" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1446" data-mp="td1rvpziabsz">
            <div className="flex flex-wrap items-center gap-6 bg-surface p-6" data-id="element-1447" data-mp="ocmhn7aitkf8">
              {([{
              color: 'bg-healthy',
              label: 'checking'
            }, {
              color: 'bg-brand',
              label: 'live'
            }, {
              color: 'bg-warn',
              label: 'retrying'
            }] as const).map(item => <span key={item.label} className="flex items-center gap-2.5" data-id="element-1448" data-mp="ajxxu3idlwxu">
                  <span className={`pulse-dot h-2 w-2 rounded-full ${item.color}`} data-id="element-1449" data-mp="5nfth4qyk0m1" />
                  <span className="font-mono text-[11px] text-ink-muted" data-id="element-1450" data-mp="lqkrvr7jz14">
                    {item.label}
                  </span>
                </span>)}
            </div>
            <div className="bg-shell p-6" data-id="element-1451" data-mp="3ntcccaeaw2e3">
              <div className="flex items-center gap-2.5" data-id="element-1452" data-mp="23b1k7satv209">
                <span className="pulse-dot h-2 w-2 rounded-full bg-brand-bright" data-id="element-1453" data-mp="23cl19l4b5h33" />
                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-white/60" data-id="element-1454" data-mp="27airmr9te982">
                  the watch is running
                </span>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-white/55" data-id="element-1455" data-mp="2h98b2ohd01qf">
                Also proposed: replace the &ldquo;Check now&rdquo; spinner with a
                single 240ms arc sweep, so the product&apos;s one manual action
                uses the brand&apos;s own gesture.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1456" data-mp="1ybf5fefcpdpc">
          <PanelHeader title="8 · Brand surfaces" description="Where the identity shows up outside the interface." level={2} data-id="element-1457" data-mp="1ybowl5c4tqwm" />
          <div className="grid gap-px bg-line lg:grid-cols-2" data-id="element-1458" data-mp="15v4erbnau1i7">
            <div className="bg-surface p-6" data-id="element-1459" data-mp="15t9wk53ewnht">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1460" data-mp="37js88q0anx35">
                Sender identities
              </p>
              <ul className="mt-4 divide-y divide-line" data-id="element-1461" data-mp="33k9ohxk7ls1w">
                {senders.map(sender => <li key={sender.address} className="py-3" data-id="element-1462" data-mp="3hbefkb9ihc0m">
                    <p className="font-mono text-[13px] text-ink" data-id="element-1463" data-mp="3cj4sr43p0s2k">
                      {sender.address}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted" data-id="element-1464" data-mp="3r6pxf2g7uzcl">{sender.use}</p>
                  </li>)}
              </ul>
            </div>

            <div className="bg-surface p-6" data-id="element-1465" data-mp="4439s5miroef">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1466" data-mp="431nf501zfhe">
                Invoice letterhead
              </p>
              <div className="mt-4 rounded-card border border-line bg-canvas p-5" data-id="element-1467" data-mp="25uoet7nuxcee">
                <div className="flex items-start justify-between gap-4 border-b border-line pb-4" data-id="element-1468" data-mp="25wrdsh6zmfv9">
                  <Wordmark size="md" tone="light" data-id="element-1469" data-mp="gdswliyuj8pu" />
                  <span className="text-right" data-id="element-1470" data-mp="gf3yfd8qe6eb">
                    <span className="block font-mono text-[13px] font-medium text-ink" data-id="element-1471" data-mp="lcw1n3ettext">
                      WT-2041
                    </span>
                    <span className="mt-1 block font-mono text-[11px] text-ink-soft" data-id="element-1472" data-mp="7kpn1lrbng0h">
                      paid · 1 Sep 2026
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-4 pt-4" data-id="element-1473" data-mp="3trnzarlwgzml">
                  <span className="text-[13px] text-ink-muted" data-id="element-1474" data-mp="2mo3z0twbgp0">
                    Pro · 15 services · monthly
                  </span>
                  <span className="font-mono text-lg font-medium text-ink" data-id="element-1475" data-mp="3ktu1fc8hlxxb">
                    $15.00
                  </span>
                </div>
              </div>
              <p className="mt-4 text-[13px] leading-relaxed text-ink-muted" data-id="element-1476" data-mp="1j0e6w49a3yx2">
                Also needed: a 512px square export of the mark for the favicon.
                I can produce it, but uploading it is done through the PUBLISH
                button in the top right — I cannot set a favicon from here.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" className="border-brand-border bg-brand-soft" data-id="element-1477" data-mp="2my8b6j7u6gql">
          <div className="px-5 py-5 sm:px-6" data-id="element-1478" data-mp="2n0cjddcrq1wo">
            <h2 className="text-sm font-semibold text-brand-ink" data-id="element-1479" data-mp="2i0gfde42mah4">
              If you only want a subset
            </h2>
            <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-brand-ink/85" data-id="element-1480" data-mp="3ep5vp001uv0z">
              Lowest risk, highest return: the <strong data-id="element-1481" data-mp="3aqgd8t05mnah">mono wordmark (B)</strong>
              , <strong data-id="element-1482" data-mp="35wc4q5b8q77a">CertSeal + SignalArc</strong>, and the{' '}
              <strong data-id="element-1483" data-mp="30yapjvgxqs13">DarkTower empty state</strong>. The beacon status set and
              the arc quota gauge change how people read data, so they are worth
              a second look before committing.
            </p>
          </div>
        </Panel>
      </div>
    </>;
}